import { Suspense, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle, Skeleton } from '@crestrewards/ui';

import ErrorBoundary from './ErrorBoundary';
import Sidebar from './Sidebar';
import Topbar from './Topbar';

/**
 * The signed-in shell: persistent sidebar, topbar, routed content.
 *
 * Tablet collapses the sidebar to an icon rail; below that it moves into a
 * Dialog drawer so the nav is still reachable without a hamburger-only layout.
 *
 * Two wrappers sit around the routed page, both of them here rather than around
 * the whole app so that **the sidebar and topbar survive whatever the page
 * does**:
 *
 *   - `ErrorBoundary` — a page that throws while rendering shows a recoverable
 *     message instead of blanking the console, and the operator can click
 *     somewhere else.
 *   - `Suspense` — every routed page is loaded on demand (`AppRoutes`), so the
 *     first visit to a screen needs somewhere to wait. Skeletons, never a
 *     spinner, per the UI rules.
 *
 * Both are inside `<main key={pathname}>`, which is what makes the boundary
 * reset itself on navigation: a new pathname is a new `<main>`, so the crashed
 * subtree is thrown away rather than remembered.
 */
export default function AppLayout() {
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="flex min-h-screen bg-background">
      {/*
        `sticky top-0 h-screen`, not `h-full`.

        Sidebar's own class list carries `h-full`, which is `height: 100%` — and
        a percentage height resolves against the parent's *height*, which here is
        only `min-h-screen`. With no definite height to resolve against, it
        computes to `auto`, so the sidebar was exactly as tall as its seven nav
        items and its right-hand border stopped partway down the page. It looked
        like a half-drawn divider, because it was one.

        `h-screen` is definite, so the border now runs the full viewport height,
        and `sticky top-0` keeps the nav in place while a long table scrolls
        beside it. tailwind-merge resolves the conflict in favour of the class
        passed here, since `className` is applied after Sidebar's own defaults.
        The drawer copy below keeps `h-full`, which is correct there — its
        DialogContent parent does have a definite height.
      */}
      <Sidebar className="sticky top-0 hidden h-screen w-16 shrink-0 md:flex lg:w-60" />

      <Dialog open={navOpen} onOpenChange={setNavOpen}>
        {/* `max-h-full overflow-y-hidden` undoes the 85vh cap and the scroll that
            DialogContent gives every dialog: this one is a full-height drawer,
            not a centred box, and the nav inside it does its own scrolling.
            **`max-h-none` does not work here** — tailwind-merge leaves it
            alongside the arbitrary `max-h-[85vh]` instead of replacing it, so
            the drawer would still be capped. Verified through cn(). */}
        <DialogContent className="left-0 top-0 h-full max-h-full max-w-[15rem] translate-x-0 translate-y-0 gap-0 overflow-y-hidden rounded-none border-y-0 border-l-0 bg-card p-0 sm:rounded-none">
          <DialogTitle className="sr-only">Navigation</DialogTitle>
          <Sidebar className="border-r-0" onNavigate={() => setNavOpen(false)} />
        </DialogContent>
      </Dialog>

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar onOpenNav={() => setNavOpen(true)} />
        {/* Keyed on pathname so each page starts at the top, with no transition
            — and so a crashed page is discarded when the operator navigates
            away rather than following them around. */}
        <main key={location.pathname} className="flex-1">
          <ErrorBoundary>
            <Suspense
              fallback={
                <div className="mx-auto w-full max-w-[1400px] space-y-6 px-4 py-6 sm:px-6 sm:py-8">
                  <div className="space-y-2">
                    <Skeleton className="h-5 w-56" />
                    <Skeleton className="h-3 w-80" />
                  </div>
                  <Skeleton className="h-64 w-full" />
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
